import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';

import { AnalyticsProvider } from '@treams/treams-analytics';
import { useFeatureDetails, useViewerRoles, useWorkspace } from '@treams/treams-hooks';
import {
	FeatureName,
	useAgentChatsQuery,
	useEnableWorkspaceAiPlanMutation,
	useFeaturesQuery,
} from '@treams/treams-network';
import { SidebarLayout } from '@treams/treams-ui';

import { AgentChatDetail } from './components/AgentChatDetail';
import { AgentChatSidebar } from './components/AgentChatSidebar';
import { EnableAiPlan } from './components/EnableAiPlan';

export default function AIAgent() {
	const chatsQuery = useAgentChatsQuery();
	const { isWorkspaceSuperuser } = useViewerRoles();
	const workspace = useWorkspace();
	const featuresQuery = useFeaturesQuery({
		variables: {
			id: workspace.id,
		},
	});
	const advancedAgent = useFeatureDetails(FeatureName.AdvancedAgent);
	const aiAgent = useFeatureDetails(FeatureName.AiAgent);
	const [enableWorkspaceAiPlan, enableState] = useEnableWorkspaceAiPlanMutation({
		refetchQueries: ['Features'],
		awaitRefetchQueries: true,
	});

	const aiInPlan = featuresQuery.data?.workspace.features.some(
		(feature) => feature.feature.id === FeatureName.AiAgent && feature.inPlan,
	);

	const chats = chatsQuery.data?.agentChats ?? [];

	const content = (
		<SidebarLayout
			fill
			sidebarWidth={240}
			sidebar={<AgentChatSidebar chats={chats} loading={chatsQuery.loading} />}
		>
			<Switch>
				<Route exact path="/assistant/new">
					<AgentChatDetail key="new" chats={chats} onChatPersisted={chatsQuery.refetch} />
				</Route>
				<Route exact path="/assistant/:chatID">
					{({ match }: RouteComponentProps<{ chatID: string }>) => (
						<AgentChatDetail
							key={match!.params.chatID!}
							chatID={match!.params.chatID!}
							chats={chats}
							onChatPersisted={chatsQuery.refetch}
						/>
					)}
				</Route>
				<Route exact path="/assistant">
					<Redirect to="/assistant/new" />
				</Route>
			</Switch>
		</SidebarLayout>
	);

	if (advancedAgent.loading || aiAgent.loading || featuresQuery.loading) return null;

	if (!aiInPlan) {
		return (
			<EnableAiPlan
				workspaceName={featuresQuery.data?.workspace.displayName ?? workspace.id}
				isWorkspaceSuperuser={isWorkspaceSuperuser}
				enabling={enableState.loading}
				onEnable={() => void enableWorkspaceAiPlan()}
			/>
		);
	}

	if (!aiAgent.available) return null;

	if (!advancedAgent.available) return content;

	return <AnalyticsProvider>{content}</AnalyticsProvider>;
}
